import { useState } from 'react';
import { X, Check, Upload, CircleDashed } from 'lucide-react';
import AttachmentModal, { type CategoryAttachment } from './AttachmentModal';

export interface MediaRequest {
  key: string;
  label: string;
  type: 'avatar' | 'image' | 'video' | 'document';
  required: boolean;
  maxCount?: number;
  description?: string;
}

interface MediaChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: string;
  mediaRequests: MediaRequest[];
  attachments: CategoryAttachment[];
  onAttachmentsChange: (attachments: CategoryAttachment[]) => void;
  /** Whether the recipient already has an avatar (auto-satisfies the photo item). */
  hasAvatar: boolean;
  onProfilePhotoUploaded?: () => void;
}

/**
 * "Complete Media Upload Requests" — a soft checklist of media the show asks the
 * recipient to upload. Each item opens the existing AttachmentModal (tagged with
 * the item's requestKey); the avatar item reuses the profile-photo upload path.
 * Never blocks the interview.
 */
export default function MediaChecklistModal({
  isOpen,
  onClose,
  categoryId,
  mediaRequests,
  attachments,
  onAttachmentsChange,
  hasAvatar,
  onProfilePhotoUploaded,
}: MediaChecklistModalProps) {
  // The currently-open uploader: a requestKey, the literal 'avatar', or null.
  const [activeItem, setActiveItem] = useState<MediaRequest | null>(null);
  const [avatarOpen, setAvatarOpen] = useState(false);

  if (!isOpen) return null;

  // Synthesize the avatar item at the top (never stored server-side).
  const avatarItem: MediaRequest = {
    key: 'avatar',
    label: 'Profile photo',
    type: 'avatar',
    required: false,
  };
  const items: MediaRequest[] = [
    avatarItem,
    ...mediaRequests.filter((r) => r.type !== 'avatar'),
  ];

  const isDone = (item: MediaRequest): boolean => {
    if (item.type === 'avatar') return hasAvatar;
    return attachments.some((a) => a.requestKey === item.key);
  };

  const doneCount = items.filter(isDone).length;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Complete Media Upload Requests
            </h2>
            <p className="text-xs text-gray-500">
              {doneCount}/{items.length} complete
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto space-y-2">
          {items.map((item) => {
            const done = isDone(item);
            return (
              <div
                key={item.key}
                className="flex items-center gap-3 rounded-xl border border-gray-200 p-3"
              >
                <span
                  className={
                    done
                      ? 'flex h-6 w-6 items-center justify-center rounded-full bg-green-500 text-white shrink-0'
                      : 'flex h-6 w-6 items-center justify-center rounded-full text-gray-300 shrink-0'
                  }
                >
                  {done ? <Check size={14} /> : <CircleDashed size={18} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.label}
                    </p>
                    {item.required && !done && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 rounded-full px-1.5 py-0.5">
                        Required
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <p className="text-xs text-gray-500 truncate">
                      {item.description}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    if (item.type === 'avatar') setAvatarOpen(true);
                    else setActiveItem(item);
                  }}
                  className="shrink-0 inline-flex items-center gap-1 rounded-full border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  <Upload size={14} />
                  {done ? 'Replace' : 'Upload'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-item uploader (non-avatar) */}
      {activeItem && (
        <AttachmentModal
          isOpen
          onClose={() => setActiveItem(null)}
          categoryId={categoryId}
          existingAttachments={attachments}
          onAttachmentsChange={onAttachmentsChange}
          requestKey={activeItem.key}
        />
      )}

      {/* Avatar uploader */}
      {avatarOpen && (
        <AttachmentModal
          isOpen
          onClose={() => setAvatarOpen(false)}
          categoryId={categoryId}
          existingAttachments={attachments}
          onAttachmentsChange={onAttachmentsChange}
          showProfilePhotoUpload
          onProfilePhotoUploaded={onProfilePhotoUploaded}
        />
      )}
    </div>
  );
}
